import { Box, ButtonBase, Typography, useMediaQuery } from '@mui/material';
import clsx from 'clsx';

type SettingsActionRowActionProps =
    | {
          href?: string;
          target?: string;
          rel?: string;
          onClick?: never;
      }
    | {
          onClick: () => void;
          href?: never;
          target?: never;
          rel?: never;
      };

type SettingsActionRowProps = SettingsActionRowActionProps & {
    icon: React.ReactNode;
    title: React.ReactNode;
    description: React.ReactNode;
    trailingAction: React.ReactNode;
    className?: string;
    disabled?: boolean;
    stopTrailingClickPropagation?: boolean;
    trailingActionFullWidthOnMobile?: boolean;
};

export const SettingsActionRow = ({
    icon,
    title,
    description,
    trailingAction,
    className,
    disabled,
    stopTrailingClickPropagation,
    trailingActionFullWidthOnMobile = true,
    ...actionProps
}: SettingsActionRowProps) => {
    const trailingActionWrapperProps = stopTrailingClickPropagation
        ? {
              onClick: (event: React.MouseEvent) => {
                  event.stopPropagation();
              },
              onPointerDown: (event: React.PointerEvent) => {
                  event.stopPropagation();
              },
          }
        : undefined;

    const isBelowMd = useMediaQuery((theme) => theme.breakpoints.down('md'));

    return (
        <ButtonBase
            focusRipple
            disabled={disabled}
            className={clsx(
                'w-full black-border rounded-[5px] px-[16px] py-[18px] bg-white text-left',
                'flex justify-between gap-[12px]',
                {
                    'flex-row items-center': !isBelowMd,
                    'flex-col items-start': isBelowMd && trailingActionFullWidthOnMobile,
                },
                className
            )}
            {...actionProps}
        >
            <Box className="flex items-center gap-[14px] min-w-0">
                <Box className="black-border rounded p-[10px] bg-neutral-100 shrink-0">{icon}</Box>
                <Box className="min-w-0">
                    <Typography fontFamily="museo-sans" fontSize={16} fontWeight={600} className="truncate">
                        {title}
                    </Typography>
                    <Typography color="neutral-800" fontSize={13}>
                        {description}
                    </Typography>
                </Box>
            </Box>
            <Box
                className={clsx('shrink-0', { 'w-full': isBelowMd && trailingActionFullWidthOnMobile })}
                {...trailingActionWrapperProps}
            >
                {trailingAction}
            </Box>
        </ButtonBase>
    );
};
